import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// Validation schema
const consumableSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional().or(z.literal('')),
  partNumber: z.string().max(50, 'Part number must be less than 50 characters').optional().or(z.literal('')),
  categoryId: z.string().min(1, 'Category is required'),
  minimumStock: z.string().min(1, 'Minimum stock is required'),
  unit: z.string().min(1, 'Unit is required').max(20, 'Unit must be less than 20 characters'),
});

type ConsumableFormData = z.infer<typeof consumableSchema>;

interface SelectOption {
  id: string;
  name: string;
}

export default function ConsumableFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditMode = Boolean(id);

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConsumableFormData>({
    resolver: zodResolver(consumableSchema),
    defaultValues: {
      name: '',
      description: '',
      partNumber: '',
      categoryId: '',
      minimumStock: '10',
      unit: 'pcs',
    },
  });

  // Fetch existing consumable for edit mode
  const { data: consumable, isLoading: consumableLoading } = useQuery({
    queryKey: ['consumable', id],
    queryFn: async () => {
      const response = await api.get(`/consumables/items/${id}`);
      return response.data.data;
    },
    enabled: isEditMode,
  });

  // Fetch categories for dropdown
  const { data: categories } = useQuery({
    queryKey: ['consumable-categories'],
    queryFn: async () => {
      const response = await api.get('/consumables/categories');
      return response.data.data || [];
    },
  });

  // Populate form for edit mode
  useEffect(() => {
    if (consumable && isEditMode) {
      reset({
        name: consumable.name || '',
        description: consumable.description || '',
        partNumber: consumable.partNumber || '',
        categoryId: consumable.categoryId || consumable.category?.id || '',
        minimumStock: consumable.minimumStock?.toString() || '10',
        unit: consumable.unit || 'pcs',
      });
    }
  }, [consumable, isEditMode, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ConsumableFormData) => {
      const payload = {
        ...data,
        minimumStock: parseInt(data.minimumStock, 10),
        description: data.description || undefined,
        partNumber: data.partNumber || undefined,
      };
      const response = await api.post('/consumables/items', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumables'] });
      toast({
        title: 'Success',
        description: 'Consumable item created successfully',
      });
      navigate('/consumables');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create consumable item',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ConsumableFormData) => {
      const payload = {
        ...data,
        minimumStock: parseInt(data.minimumStock, 10),
        description: data.description || undefined,
        partNumber: data.partNumber || undefined,
      };
      const response = await api.put(`/consumables/items/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumables'] });
      queryClient.invalidateQueries({ queryKey: ['consumable', id] });
      toast({
        title: 'Success',
        description: 'Consumable item updated successfully',
      });
      navigate('/consumables');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update consumable item',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: ConsumableFormData) => {
    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditMode && consumableLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/consumables">
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditMode ? 'Edit Consumable' : 'Add New Consumable'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update consumable item information' : 'Create a new consumable item'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    error={errors.name?.message}
                    placeholder="e.g., HP Toner Cartridge 58A"
                    className="mt-1"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="partNumber">Part Number</Label>
                    <Input
                      id="partNumber"
                      {...register('partNumber')}
                      error={errors.partNumber?.message}
                      placeholder="e.g., CF258A"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="categoryId">Category *</Label>
                    <select
                      id="categoryId"
                      {...register('categoryId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select category...</option>
                      {(categories as SelectOption[] || []).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {errors.categoryId && (
                      <p className="mt-1 text-sm text-destructive">{errors.categoryId.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    {...register('description')}
                    className="w-full min-h-[100px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    placeholder="Enter item description..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-destructive">{errors.description.message}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stock Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Stock Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="minimumStock">Minimum Stock Level *</Label>
                    <Input
                      id="minimumStock"
                      type="number"
                      min="0"
                      {...register('minimumStock')}
                      error={errors.minimumStock?.message}
                      placeholder="e.g., 10"
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Alert will be triggered when stock falls below this level
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="unit">Unit of Measurement *</Label>
                    <select
                      id="unit"
                      {...register('unit')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="pcs">Pieces (pcs)</option>
                      <option value="box">Box</option>
                      <option value="pack">Pack</option>
                      <option value="roll">Roll</option>
                      <option value="ream">Ream</option>
                      <option value="bottle">Bottle</option>
                      <option value="kg">Kilogram (kg)</option>
                      <option value="liter">Liter</option>
                      <option value="meter">Meter</option>
                      <option value="set">Set</option>
                    </select>
                    {errors.unit && (
                      <p className="mt-1 text-sm text-destructive">{errors.unit.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {isEditMode ? 'Update Consumable' : 'Create Consumable'}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/consumables')}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Help Text */}
            <Card>
              <CardContent className="p-6">
                <h4 className="font-medium mb-2">Tips</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Part numbers help with reordering</li>
                  <li>• Set minimum stock to get alerts</li>
                  <li>• Choose the right unit for accuracy</li>
                  <li>• Categories help organize items</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
